import { Injectable, inject } from '@angular/core';
import { LocalizationService, LocalizationParam } from '@abp/ng.core';
import { Toaster } from '@abp/ng.theme.shared';
import { MessageService } from 'primeng/api';

/**
 * `LocalizationParam` (from `@abp/ng.core`) is only `string | LocalizationWithDefault` — it carries no
 * interpolation params, so callers with a dynamic message (e.g. a username) previously had to pre-resolve
 * via `LocalizationService.instant()` themselves, which double-localizes when passed through here and
 * violates the "always pass a localization key" convention (`mandatory-services.md`). This lets callers
 * pass the key and its interpolation args together, resolved once, right here.
 */
export interface LocalizationParamWithArgs {
  key: string;
  params: (string | number)[];
}
export type ToasterLocalizationParam = LocalizationParam | LocalizationParamWithArgs;

function hasArgs(value: ToasterLocalizationParam): value is LocalizationParamWithArgs {
  return typeof value === 'object' && value !== null && 'params' in value;
}

const SEVERITY_MAP: Record<Toaster.Severity, string> = {
  neutral: 'secondary',
  success: 'success',
  info: 'info',
  warning: 'warn',
  error: 'error',
};

const DEFAULT_SUMMARY_KEY: Record<Toaster.Severity, string> = {
  neutral: '::Information',
  success: '::Success',
  info: '::Information',
  warning: '::Warning',
  error: '::Error',
};

@Injectable({
  providedIn: 'root',
})
export class ToasterService implements Toaster.Service {
  private readonly messageService = inject(MessageService);
  private readonly localizationService = inject(LocalizationService);

  error(
    message: ToasterLocalizationParam,
    title?: ToasterLocalizationParam,
    options?: Partial<Toaster.ToastOptions>,
  ): Toaster.ToasterId {
    return this.show(message, title, 'error', options);
  }

  info(
    message: ToasterLocalizationParam,
    title?: ToasterLocalizationParam,
    options?: Partial<Toaster.ToastOptions>,
  ): Toaster.ToasterId {
    return this.show(message, title, 'info', options);
  }

  success(
    message: ToasterLocalizationParam,
    title?: ToasterLocalizationParam,
    options?: Partial<Toaster.ToastOptions>,
  ): Toaster.ToasterId {
    return this.show(message, title, 'success', options);
  }

  warn(
    message: ToasterLocalizationParam,
    title?: ToasterLocalizationParam,
    options?: Partial<Toaster.ToastOptions>,
  ): Toaster.ToasterId {
    return this.show(message, title, 'warning', options);
  }

  show(
    message: ToasterLocalizationParam,
    title: ToasterLocalizationParam,
    severity: Toaster.Severity,
    options?: Partial<Toaster.ToastOptions>,
  ): Toaster.ToasterId {
    const detail = this.resolveLocalization(message);
    const rawTitle = title ? this.resolveLocalization(title) : '';
    const summary = rawTitle || this.localizationService.instant(DEFAULT_SUMMARY_KEY[severity]);

    this.messageService.add({
      severity: SEVERITY_MAP[severity] ?? 'info',
      summary,
      detail,
      // Overrides PrimeNG's default per-severity icon. PrimeNG's Toast template always renders this as
      // `pi ${icon}` (see ToastStyle's `messageIcon` classMap) — it only works with a PrimeIcons glyph
      // *name* (e.g. "pi-megaphone"), not another icon library's full class (e.g. "fas fa-bullhorn" would
      // collide: both `.pi` and `.fas` set `font-family`, and the wrong one wins the cascade, so nothing
      // renders). A color utility class can still ride along in the same string, e.g.
      // "pi-megaphone acx-toast-icon--primary".
      icon: options?.iconClass,
    });

    return 0;
  }

  clear(): void {
    this.messageService.clear();
  }

  remove(_id: number): void {
    // PrimeNG does not support remove-by-id; no-op for interface compliance
  }

  private resolveLocalization(value: ToasterLocalizationParam): string {
    if (hasArgs(value)) {
      return this.localizationService.instant(value.key, ...value.params.map(String));
    }
    return this.localizationService.instant(value);
  }
}
